import { useNavigate, useParams, type NavigateOptions } from 'react-router-dom';

/**
 * Hook that provides navigation functions with automatic business ID prefixing.
 * Use this instead of useNavigate() when navigating within business-scoped routes.
 */
export function useBusinessNav() {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();

  /**
   * Navigate to a path within the current business context.
   * Paths starting with '/' are prefixed with /{businessId}
   * @example nav('/invoices') -> navigates to /bus_abc123/invoices
   */
  const nav = (path: string, options?: NavigateOptions) => {
    const fullPath = path.startsWith('/')
      ? `/${businessId}${path}`
      : path;
    navigate(fullPath, options);
  };

  /**
   * Get the full path for a business-scoped route.
   * Useful for building href values for links.
   * @example getPath('/invoices') -> '/bus_abc123/invoices'
   */
  const getPath = (path: string): string => {
    return path.startsWith('/')
      ? `/${businessId}${path}`
      : path;
  };

  return { nav, getPath, businessId, navigate };
}
